import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const FAB_GAP_PX = 12;
const FAB_SIZE_PX = 64;
const DESKTOP_BOTTOM_REM = 1.5;

function measureBottom(navRef, footerRef) {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );

  let defaultBottom = DESKTOP_BOTTOM_REM * rootFontSize;

  if (isMobile && navRef.current) {
    defaultBottom = navRef.current.getBoundingClientRect().height + FAB_GAP_PX;
  } else if (isMobile) {
    defaultBottom = 6.25 * rootFontSize;
  }

  let bottom = defaultBottom;

  if (footerRef.current) {
    const footerTop = footerRef.current.getBoundingClientRect().top;
    const viewportHeight = window.innerHeight;
    const footerTopFromBottom = viewportHeight - footerTop;
    const fabTopFromBottom = defaultBottom + FAB_SIZE_PX;

    // Lift FAB only when footer would overlap its default spot (above nav),
    // not whenever footer is visible anywhere on screen.
    if (
      footerTop < viewportHeight &&
      footerTopFromBottom < fabTopFromBottom + FAB_GAP_PX
    ) {
      bottom = Math.max(defaultBottom, footerTopFromBottom + FAB_GAP_PX);
    }
  }

  return Math.round(bottom);
}

export function useFabPosition({ navRef, footerRef, enabled }) {
  const { pathname } = useLocation();
  const [bottomPx, setBottomPx] = useState(null);

  const update = useCallback(() => {
    if (!enabled) return;
    setBottomPx(measureBottom(navRef, footerRef));
  }, [enabled, footerRef, navRef]);

  useLayoutEffect(() => {
    if (!enabled) {
      setBottomPx(null);
      return;
    }
    setBottomPx(measureBottom(navRef, footerRef));
  }, [enabled, pathname, footerRef, navRef]);

  useEffect(() => {
    if (!enabled) return;

    update();

    const onScroll = () => update();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', onScroll);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(update)
        : null;

    if (resizeObserver) {
      if (navRef.current) resizeObserver.observe(navRef.current);
      if (footerRef.current) resizeObserver.observe(footerRef.current);
    }

    // Lazy routes change layout after paint — recalculate once content settles.
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });
    const timeoutId = window.setTimeout(update, 150);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [enabled, pathname, update, footerRef, navRef]);

  return bottomPx;
}
